<template>
    <Head>
        <component :is="'script'" type="application/ld+json" v-html="JSON.stringify(schema)" />
    </Head>
    <div class="w-full flex flex-wrap text-slate-300">
        <!-- Newish Music -->
        <div class="w-full md:w-1/2 pr-3">
            <h2 class="header-class">
                <vue-feather type="music" size="18px" class="-mb-[2px]" />
                Newish Music
            </h2>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div
                    v-for="m in upcoming"
                    :ref="`ref-${m.id}`"
                    :key="m.id"
                    class="newish group relative overflow-hidden rounded border border-slate-700/40 bg-slate-900/30 flex flex-col"
                >
                    <div class="relative overflow-hidden">
                        <img
                            :ref="`img-${m.id}`"
                            class="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-105"
                            :src="`/storage/music_albums/${m.lidarr_id}.webp`"
                            :alt="m.title"
                            @error="imageLoadError(m.id, 'img')"
                            @load="onImageLoad(m.id, 'img')"
                        >
                        <div class="absolute top-2 left-2 bg-black/70 backdrop-blur-sm text-xs text-slate-300 px-2 py-0.5 rounded-full font-medium uppercase tracking-wide">
                            {{ formatDate(m.release_date, 'MMM D, YYYY') }}
                        </div>
                        <div class="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent flex flex-col justify-end p-2">
                            <p class="text-xs text-slate-300 truncate uppercase leading-tight">
                                <a :href="getLink(m.artist)" class="hover:text-white">{{ m.artist?.name }}</a>
                            </p>
                            <h3 class="text-sm font-semibold text-white leading-tight truncate drop-shadow">{{ m.title }}</h3>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Last Plays -->
        <div class="w-full md:w-1/2 pl-4">
            <h2 class="header-class"><vue-feather type="speaker" size="18px" class="-mb-[2px]" /> Last Plays</h2>
            <ul class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 pb-32 last-plays">
                <li
                    v-for="m in lastPlayedLocal"
                    :key="m.id"
                    class="last-play overflow-hidden rounded border border-slate-700/40 bg-slate-900/30 flex flex-col"
                >
                    <div class="relative overflow-hidden">
                        <img
                            :ref="`imglp-${m.id}`"
                            class="w-full aspect-square object-cover"
                            :src="m.image_path ? `/${m.image_path}` : `/storage/recipes/no-image.jpg`"
                            :alt="`${m.grandparent_title} - ${m.title}`"
                            @load="onImageLoad(m.id, 'imglp')"
                            @error="imageLoadError(m.id, 'imglp')"
                        >
                        <div class="title-shadow absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent flex flex-col justify-end p-2">
                            <p class="text-xs italic text-secondary-light truncate leading-tight">{{ m.grandparent_title }}</p>
                            <p class="text-xs font-semibold text-white truncate">{{ m.title }}</p>
                            <p class="text-xs text-slate-100">{{ unix(m.date) }}</p>
                        </div>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
    import GuestLayout from '@/Layouts/GuestLayout.vue';
    import { formatDate } from '@/utils/Utilities';
    import { gsap } from 'gsap/all'
    import { Head } from '@inertiajs/vue3'
    import dayjs from 'dayjs'

    export default {
        name: "MusicComp",
        layout: GuestLayout,
        components: { Head },
        props: ['upcoming', 'lastPlayed'],
        computed: {
            schema() {
                return {
                    '@context': 'https://schema.org',
                    '@type': 'CollectionPage',
                    name: 'Music to hear',
                    description: 'Recently played tracks and new music releases',
                    mainEntity: [
                        {
                            '@type': 'ItemList',
                            name: 'Recently Played',
                            itemListElement: this.lastPlayedLocal.map((m, i) => ({
                                '@type': 'ListItem',
                                position: i + 1,
                                item: {
                                    '@type': 'MusicRecording',
                                    name: m.title,
                                    byArtist: { '@type': 'MusicGroup', name: m.grandparent_title },
                                    inAlbum: { '@type': 'MusicAlbum', name: m.parent_title },
                                },
                            })),
                        },
                        {
                            '@type': 'ItemList',
                            name: 'New Releases',
                            itemListElement: (this.upcoming ?? []).map((m, i) => ({
                                '@type': 'ListItem',
                                position: i + 1,
                                item: {
                                    '@type': 'MusicAlbum',
                                    name: m.title,
                                    byArtist: { '@type': 'MusicGroup', name: m.artist?.name },
                                    datePublished: m.release_date,
                                },
                            })),
                        },
                    ],
                };
            },
        },
        data() {
            return {
                lastPlayedLocal: (this.lastPlayed ?? []).slice(0, 32),
            }
        },
        mounted() {
            window.Echo.channel('music').listen('.track.played', (e) => {
                this.lastPlayedLocal.unshift(e.track);
                this.lastPlayedLocal = this.lastPlayedLocal.slice(0, 20);
                this.$nextTick(() => {
                    gsap.fromTo('.last-play:first-child', { y: -20, opacity: 0 }, { y: 0, opacity: 1, duration: .4 });
                });
            });

            gsap.fromTo('.newish',
                { y: -10, opacity: 0 },
                { y: 0, opacity: 1, stagger: .02, duration: .2, delay: .2, onComplete: () => {
                    gsap.fromTo('.last-play',
                        { y: 10, opacity: 0 },
                        { y: 0, opacity: 1, stagger: .02, duration: .2 },
                    )
                }},
            )
        },
        methods: {
            formatDate,
            unix(u) {
                return dayjs.unix(u).format('MMM D, YYYY h:mma');
            },
            getLink(a) {
                if (!a?.links) return null;
                const link = a.links?.find(a => a.name === 'allmusic');
                return link?.url ?? null;
            },
            imageLoadError(id, t) {
                this.$refs[`${t}-${id}`][0].src = `/storage/recipes/no-image.jpg`
            },
            onImageLoad(id, t) {
                const im = this.$refs[`${t}-${id}`];
                gsap.to(im, { opacity: 1, stagger: .03, duration: 1 })
            },
        }
    }
</script>

<style lang="postcss">
.last-play, .newish {
    opacity: 0;
}
</style>
